import { FileLog } from "./file";
import { Resource } from "./resource";
import { UnitDTO } from "./unit";
import { BuildingDTO } from "./building";
import { YallyCompany } from "./yally-company";

export enum ContractLegalForm {
  RESIDENTIAL_SHORT_FLANDERS = "Residentieel korte duur Vlaanderen",
  RESIDENTIAL_LONG_FLANDERS = "Residentieel lange duur Vlaanderen",
  RETAIL_LONG_FLANDERS = "Retail lange duur Vlaanderen",
  RETAIL_SHORT_FLANDERS = "Retail korte duur Vlaanderen",
  COMMON_LAW_FLANDERS = "Gemeenrecht Vlaanderen",
  PARKING = "Parking",
}

export enum PropertyTaxes {
  TO_CHECK = "Na te kijken",
  FOR_TENANT = "Volledig ten laste huurder",
  PARTIAL_TENANT = "Gedeeltelijk ten laste huurder",
  FOR_LANDLORD = "Volledig ten laste verhuurder",
}

export enum ContractPhase {
  CONCEPT = "In concept",
  ACTIVE = "Lopende contracten",
  TERMINATION_PHASE = "Contract in opzeg",
  CLOSED = "Gesloten",
}

export enum InvoicingFrequency {
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
}

export enum ValidGuaranteeType {
  BANK_ACCOUNT_LANDLORD = "Op rekening Yally",
  KORFINE = "Korfine",
  BLOCKED_ACCOUNT = "Geblokkeerde rekening",
  FIRST_REQUEST = "Bankwaarborg op eerste verzoek",
}

export enum GuaranteeType {
  TO_CHECK = "Na te kijken",
  NO_GUARANTEE = "Geen bankwaarborg aanwezig",
  BANK_ACCOUNT_LANDLORD = "Op rekening Yally",
  KORFINE = "Korfine",
  BLOCKED_ACCOUNT = "Geblokkeerde rekening",
  FIRST_REQUEST = "Bankwaarborg op eerste verzoek",
}

export enum PriceChangeMetadata {
  INIT = "Initieel niveau",
  DAILY_RECORD = "Dagelijkse opname",
  INDEXATION = "Indexatie",
  DISCOUNT = "Korting",
  UPSIDE = "Upside",
}

export type Price = {
  id: number;
  product: ContractProduct;
  amount: number;
  changeMetaData: PriceChangeMetadata;
  recordDate: any;
  effectiveDate: any;
};

export type ProductTemplate = {
  id: number;
  name: string;
  products: ContractProduct;
  company: YallyCompany;
  ledger: number;
  indexable: boolean;
  odooProductVariantId: number;
  odooTemplateName: string;
};

export type ContractProduct = {
  id: number;
  contract: RentalContract;
  name: string;
  odooProductId: number;
  ledger: number;
  price: Price[];
  indexable: boolean;
  template: ProductTemplate;
  odooOrderLineId: string;
  buildingAttribute?: {
    id: number;
    name: string;
    description?: string;
  };
};

export type RentalGuarantee = {
  id?: number;
  contract: RentalContract;
  fileLogId?: string;
  fileLog?: FileLog;
  amount: number;
  type: GuaranteeType;
  reference: string;
};

export enum MilestoneDateType {
  START_DATE = "Startdatum",
  SIGN_DATE = "Ondertekeningsdatum",
  END_DATE = "Einddatum",
  INDEXATION_DATE = "Indexatie",
  CUSTOM = "Aangepaste gebeurtenis",
  CANCELLATION_DATE = "Opzegdatum",
  DEADLINE_BREAK_DATE_LANDLORD = "Uiterste opzegdatum verhuurder",
  DEADLINE_BREAK_DATE_TENANT = "Uiterste opzegdatum huurder",
  START_RENEWAL_PERIOD = "Start hernieuwingsperiode",
  END_RENEWAL_PERIOD = "Einde hernieuwingsperiode",
  INDEX_DATE = "Indexatie verjaardag",
  TRANSITION_DATE = "Automatische verlenging naar residentieel contract lange duur",
}

export enum ShowMilestoneInOverview {
  YES = "Ja",
  NO = "Nee",
}

export enum MilestoneOrigin {
  SYSTEM = "Systeem",
  USER = "Gebruiker",
}

export type ContractMilestoneDate = {
  id?: number;
  milestoneDateType: MilestoneDateType | null;
  showMilestoneInOverview: ShowMilestoneInOverview | null;
  milestoneArchivedFromOverview: boolean;
  milestoneArchivedFromOverviewByUserId: string | null;
  origin: MilestoneOrigin | null;
  customDescription: string | null;
  rentalContract?: RentalContract | null;
  rentalContractId?: number | null;
  date: Date | null;
  userId: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export enum ContactType {
  TENANT = "Contractant (huurder)",
  INHABITANT = "Mede-bewoner",
  LAWYER = "Advocaat",
  GOVERNOR = "Bewindvoerder",
  THIRD_PARTY_PAYMENT = "Derde partij huurbetaling (vb. OCMW, fonds, ...)",
  EMPLOYER = "Werkgever",
}

export type ContractContact = {
  id?: number;
  resourceId: number | null;
  resource: Resource | null;
  contract: RentalContract | null;
  contactType: ContactType | null;
  fileLogIds?: number[];
  fileLogs?: FileLog[];
};

export type ActivePrice = {
  odooProductId: number;
  price: number;
  equalsInOdoo: boolean;
};

export enum DocumentType {
  RENTAL_CONTRACT = 'Huurcontract',
  RENTAL_PLACE_DESCRIPTION = 'Ingaande plaatsbeschrijving',
  RENTAL_GUARANTEE = 'Huurwaarborg',
  RENTAL_FIRE_INSURANCE = 'Brandverzekering huurder',
  RENTAL_REGISTRATION = 'Registratiebewijs huurcontract',
  // Contracten zonder unit (garage/parking): enkel deze drie documenten worden opgevolgd.
  GARAGE_RENTAL_CONTRACT = 'Huur garage zonder woninghuurcontract',
  GARAGE_FIRE_INSURANCE = 'Brandverzekering garage',
  GARAGE_GUARANTEE = 'Huurwaarborg garage'
}

export enum MissingFileLogLabel {
  RENTAL_CONTRACT = 'Huurcontract',
  RENTAL_PLACE_DESCRIPTION = 'Ingaande plaatsbeschrijving',
  RENTAL_GUARANTEE = 'Huurwaarborg',
  RENTAL_FIRE_INSURANCE = 'Brandverzekering huurder',
  RENTAL_REGISTRATION = 'Registratiebewijs huurcontract',
  GARAGE_RENTAL_CONTRACT = 'Huur garage zonder woninghuurcontract',
  GARAGE_FIRE_INSURANCE = 'Brandverzekering garage',
  GARAGE_GUARANTEE = 'Huurwaarborg garage'
}

export enum DocumentStatusType {
  MISSING_REQUIRED = "MISSING_REQUIRED",
  MISSING_OPTIONAL = "MISSING_OPTIONAL", 
  MISSING_NOT_REQUIRED = "MISSING_NOT_REQUIRED",
}

export type DocumentStatus = {
  id?: number;
  documentType: string;
  status: DocumentStatusType;
  reason: string;
  comment?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export enum OutGoingRentalFileStatus {
  NOT_NEEDED = 'Niet nodig',
  IN_PROGRESS = 'In uitvoering',
  COMPLETED = 'Afgerond'
}

export enum IncomingRentalFileStatus {
  NOT_STARTED = 'Niet gestart',
  IN_PROGRESS = 'In uitvoering',
  COMPLETED = 'Afgerond'
}

export type RentalContract = {
  id?: number;
  unit?: UnitDTO;
  missingDocuments?: string[];
  unitId?: string;
  /** Set instead of unitId for attribute-only contracts (e.g. a standalone parking rental). */
  buildingId?: string;
  building?: BuildingDTO;
  operationalChecks?: [];
  numResidents: number;
  guarantee?: RentalGuarantee[];
  contractContacts: ContractContact[];
  odooContractId?: number;
  invoicingFrequency?: InvoicingFrequency;
  flaggedForWatchlist?: boolean;
  flaggedForWatchlistReason?: string;
  nextInvoiceDate?: string;
  nextIndexDate?: string;
  propertyTaxes?: PropertyTaxes;
  products?: ContractProduct[];
  activeProvision?: ActivePrice;
  activePrice?: ActivePrice;
  activePropertyTaxes?: any;
  fileLogIds: any[];
  missingFileLogLabels: MissingFileLogLabel[];
  fileLogs: FileLog[];
  contractMilestoneDates?: ContractMilestoneDate[];
  phase: ContractPhase;
  legalForm?: ContractLegalForm | null;
  documentStatuses?: DocumentStatus[];
  createdAt?: Date;
  outgoingInspectionChecklistSent?: boolean;
  outgoingInspectionDate?: Date;
  visitationDays?: string;
  visitationResponsible?: string;
  guaranteeAmountToWithhold?: number;
  guaranteeReleased?: boolean;
  outgoingInspectionChecklistSentDate?: Date;
  outgoingInspectionChecklistAutoSent?: boolean;
  outgoingInspectionCalendarEventId?: string;
  outgoingInspectionResponsibleEmail?: string;
  outgoingRentalFileStatus?: OutGoingRentalFileStatus;
  outgoingChecklistMessageId?: string;
  lastIndexationDate?: Date | string | null;
  huurcontractFileLog?: {
    id: number;
    sharePointFileUrl: string;
    documentDate: Date | string;
  };
  currentEPC?: {
    label: string;
    kWh: string;
    fileLogId: number;
    sharePointFileUrl: string;
  };
  initPrice?: number;
  /**
   * Total outstanding debt (euros) summed across all incasso tickets linked
   * to this contract's unit. Only present when an incasso dossier exists.
   * Populated server-side on the watchlist endpoint.
   */
  incassoOpenstand?: number;

  incomingContractSigned?: boolean;
  incomingInspectionDate?: Date | null;
  incomingInspectionCalendarEventId?: string;
  incomingInspectionResponsibleEmail?: string | null;
  incomingGuaranteeInfoSent?: boolean;
  incomingGuaranteeReceived?: boolean;
  incomingFireInsuranceInfoSent?: boolean;
  incomingFireInsuranceReceived?: boolean;
  incomingRentalFileStatus?: IncomingRentalFileStatus;

  /**
   * Computed by the indexation-overview endpoint.
   * Positive = days until next anniversary; negative = days since it passed.
   */
  daysUntilAnniversary?: number | null;

  /**
   * Computed by the indexation-overview endpoint.
   * Number of months since the last indexation was applied.
   */
  monthsSinceLastIndexation?: number | null;

  /**
   * Which tab bucket this contract belongs to in the indexation overview.
   */
  indexationGroup?: "never" | "pending" | "indexed";

  /**
   * True when multiple active contracts share this unit.
   * Computed by the indexation-overview endpoint.
   */
  hasDuplicateActiveContracts?: boolean;
};

export type IncomingTenantItem = {
  applicationId: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  unitId: string;
  unitLabel: string;
  buildingId: string;
  buildingName: string;
  googlePlaceLongAddress?: string;
  conceptContract: RentalContract | null;
};
